import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SeasonSelector from '../components/SeasonSelector';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(n || 0);
}
function qty(n) {
  return new Intl.NumberFormat('tr-TR').format(n || 0);
}

const TR_DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const TR_MONTHS_FULL = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// 'YYYY-MM-DD' -> "06 Nisan 2026, Pazartesi" (parsed as local, no timezone shift)
function fullTrDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${d} ${TR_MONTHS_FULL[m - 1]} ${y}, ${TR_DAYS[dt.getDay()]}`;
}
function shiftDate(s, days) {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (localStorage.getItem('teldolap_onboarding_pending')) {
      navigate('/onboarding');
    }
  }, [navigate]);

  useEffect(() => {
    if (period === 'today' || period === 'season') return; // handled by their own components
    setLoading(true);
    api.get(`/dashboard?period=${period}`)
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [period]);

  const chartData = stats ? [
    { name: 'Satış', value: stats.sales.total, color: '#27ae60' },
    { name: 'Alım', value: stats.purchases.total, color: '#e74c3c' },
    { name: 'İşçilik', value: stats.labour.total, color: '#e67e22' },
  ] : [];

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', color: '#1c3a1c' }}>
          Merhaba, {user?.name?.split(' ')[0]} 👋
        </h2>
        <p style={{ fontSize: '0.85rem', color: '#7a9472', marginTop: 2 }}>
          {user?.farmName}
        </p>
      </div>

      {/* Period selector — Bugün is the default first tab */}
      <div className="period-tabs">
        {['today', 'week', 'month', 'year', 'season'].map(p => (
          <button
            key={p}
            className={`period-tab${period === p ? ' active' : ''}`}
            onClick={() => setPeriod(p)}
          >
            {t(`dashboard.period.${p}`)}
          </button>
        ))}
      </div>

      {period === 'today' ? (
        <TodayReport navigate={navigate} />
      ) : period === 'season' ? (
        <SeasonReport navigate={navigate} />
      ) : loading ? (
        <div className="empty-state"><p>Yükleniyor...</p></div>
      ) : stats ? (
        <>
          {/* Net profit — hero card */}
          <div className="card" style={{
            background: stats.netProfit >= 0
              ? 'linear-gradient(135deg, #1c3a1c, #2d5a27)'
              : 'linear-gradient(135deg, #5a1c1c, #8b2020)',
            color: '#fff',
            marginBottom: 12,
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('dashboard.netProfit')}
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: 4 }}>
              {fmt(stats.netProfit)}
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: 4 }}>
              {stats.sales.count} satış · {stats.purchases.count} alım
            </div>
          </div>

          {/* Stats grid */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="icon">💰</div>
              <div className="label">{t('dashboard.totalSales')}</div>
              <div className="value positive">{fmt(stats.sales.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">🛒</div>
              <div className="label">{t('dashboard.totalPurchases')}</div>
              <div className="value negative">{fmt(stats.purchases.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">👷</div>
              <div className="label">{t('dashboard.totalLabour')}</div>
              <div className="value negative">{fmt(stats.labour.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">🌾</div>
              <div className="label">{t('dashboard.totalHarvest')}</div>
              <div className="value">
                {qty(stats.harvest.totalKg)} kg
              </div>
            </div>
            <LabourMonthCard navigate={navigate} />
          </div>

          {/* Bar chart */}
          {(stats.sales.total > 0 || stats.purchases.total > 0) && (
            <div className="card">
              <div className="section-title" style={{ marginBottom: 12 }}>Genel Bakış</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: 'Nunito' }} />
                  <YAxis tick={{ fontSize: 10, fontFamily: 'Nunito' }} />
                  <Tooltip
                    formatter={(val) => [fmt(val), '']}
                    contentStyle={{ fontFamily: 'Nunito', borderRadius: 8, fontSize: 12 }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Stock summary */}
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '2rem' }}>📦</span>
            <div>
              <div style={{ fontWeight: 700 }}>{stats.stock.activeProducts} ürün stokta</div>
              <div style={{ fontSize: '0.8rem', color: '#7a9472' }}>Aktif stok kalemleri</div>
            </div>
          </div>

          {/* Favourite products */}
          <FavouriteProductsSection />

          {/* Notebook widget */}
          <NotebookWidget navigate={navigate} />

          {/* Upcoming payments */}
          <UpcomingPaymentsSection />
        </>
      ) : null}
    </div>
  );
}

// ─── Today day report ──────────────────────────────────────────────────────
function CollapsibleSection({ icon, title, right, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: '1.1rem' }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1c3a1c' }}>{title}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: '0.9rem' }}>{right}</span>
        <span style={{ fontSize: '0.72rem', color: '#aaa', marginLeft: 6 }}>({count} kayıt)</span>
        <span style={{ marginLeft: 6, color: '#aaa', fontSize: '0.8rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: '0 14px 10px' }}>{children}</div>}
    </div>
  );
}

function Row({ children, onClick }) {
  return (
    <div onClick={onClick}
      style={{ padding: '7px 0', borderTop: '1px solid #f2f2f2', fontSize: '0.84rem', color: '#444', cursor: onClick ? 'pointer' : 'default' }}>
      {children}
    </div>
  );
}

function TodayReport({ navigate }) {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard/today?date=${date}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [date]);

  const isToday = date === todayStr();
  const navBtn = {
    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
    width: 38, height: 38, cursor: 'pointer', fontSize: '1.2rem', color: '#2d5a27', fontWeight: 700, flexShrink: 0,
  };

  // Date selector — always visible (prev / picker / next / "Bugün")
  const selector = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button onClick={() => setDate(shiftDate(date, -1))} style={navBtn} aria-label="Önceki gün">‹</button>
        <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)}
          style={{ flex: 1, textAlign: 'center', fontWeight: 700, height: 38 }} />
        <button onClick={() => setDate(shiftDate(date, 1))} style={navBtn} aria-label="Sonraki gün">›</button>
        {!isToday && (
          <button onClick={() => setDate(todayStr())}
            style={{ background: 'var(--green-mid)', color: '#fff', border: 'none', borderRadius: 10, padding: '0 12px', height: 38, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
            Bugün
          </button>
        )}
      </div>
      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1c3a1c', marginBottom: 10 }}>
        📅 {fullTrDate(date)}
      </div>
    </div>
  );

  if (loading) return <div>{selector}<div className="empty-state"><p>Yükleniyor...</p></div></div>;
  if (!data) return <div>{selector}<div className="empty-state"><p>Günlük rapor alınamadı</p></div></div>;

  const { purchases, sales, labour, harvests, payments } = data;
  const isEmpty = !purchases.length && !sales.length && !labour.length && !harvests.length && !payments.length;

  return (
    <div>
      {selector}

      {/* Net cash hero card */}
      <div className="card" style={{
        background: data.netCash >= 0
          ? 'linear-gradient(135deg, #1c3a1c, #2d5a27)'
          : 'linear-gradient(135deg, #5a1c1c, #8b2020)',
        color: '#fff', marginBottom: 12,
      }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.75 }}>{isToday ? 'Bugünkü Net Nakit' : 'Net Nakit'}</div>
        <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: 2 }}>{fmt(data.netCash)}</div>
        <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 4 }}>
          Giren: {fmt(data.paymentsIn)} &nbsp;|&nbsp; Çıkan: {fmt(data.paymentsOut)}
        </div>
      </div>

      {isEmpty ? (
        <div className="card" style={{ textAlign: 'center', padding: '24px 16px' }}>
          <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>🌅</div>
          <p style={{ fontWeight: 600, color: '#1c3a1c', marginBottom: 4 }}>{isToday ? 'Bugün henüz kayıt yok.' : 'Bu tarihte kayıt yok.'}</p>
          <p style={{ fontSize: '0.82rem', color: '#7a9472', marginBottom: 14 }}>Aşağıdaki butonlardan hızlıca ekleyin:</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={() => navigate('/purchases')}>+ Alım</button>
            <button className="btn btn-primary" onClick={() => navigate('/sales')}>+ Satış</button>
            <button className="btn btn-primary" onClick={() => navigate('/harvest')}>+ Hasat</button>
            <button className="btn btn-primary" onClick={() => navigate('/labour')}>+ İşçilik</button>
          </div>
        </div>
      ) : (
        <>
          {sales.length > 0 && (
            <CollapsibleSection icon="💰" title="Satışlar" right={fmt(data.salesTotal)} count={sales.length}>
              {sales.map(s => (
                <Row key={s.id} onClick={() => navigate('/sales')}>
                  {s.product_name} · {qty(s.quantity)} {s.unit} · <b>{fmt(s.total_amount)}</b>
                </Row>
              ))}
            </CollapsibleSection>
          )}

          {purchases.length > 0 && (
            <CollapsibleSection icon="🛒" title="Alımlar" right={fmt(data.purchasesTotal)} count={purchases.length}>
              {purchases.map(p => (
                <Row key={p.id} onClick={() => navigate('/purchases')}>
                  {p.item_name} · {qty(p.quantity)} {p.unit} · <b>{fmt(p.total_amount)}</b>
                </Row>
              ))}
            </CollapsibleSection>
          )}

          {labour.length > 0 && (
            <CollapsibleSection icon="👷" title="İşçilik" right={fmt(data.labourTotal)} count={labour.length}>
              {labour.map(l => (
                <Row key={l.id} onClick={() => navigate('/labour')}>
                  {l.worker_name} · {l.num_workers} kişi · {qty(l.days)} gün · <b>{fmt(l.total_amount)}</b>
                </Row>
              ))}
            </CollapsibleSection>
          )}

          {harvests.length > 0 && (
            <CollapsibleSection icon="🌾" title="Hasat" right={`${qty(data.harvestTotal)} kg`} count={harvests.length}>
              {harvests.map(h => (
                <Row key={h.id} onClick={() => navigate('/harvest')}>
                  {h.product_name}{h.quality_grade ? ` · ${h.quality_grade}` : ''} · {qty(h.quantity)} {h.unit}{h.field_name ? ` · ${h.field_name}` : ''}
                </Row>
              ))}
            </CollapsibleSection>
          )}

          {payments.length > 0 && (
            <CollapsibleSection icon="💳" title="Ödemeler" right={fmt(data.paymentsIn - data.paymentsOut)} count={payments.length}>
              {payments.map(p => (
                <Row key={p.id} onClick={() => navigate('/payments')}>
                  {p.direction === 'in' ? '✅ Alındı' : '🔴 Ödendi'} · {p.notes || (p.entity_type === 'sale' ? 'Satış' : p.entity_type === 'purchase' ? 'Alım' : 'İşçilik')} · <b>{fmt(p.amount)}</b>
                </Row>
              ))}
            </CollapsibleSection>
          )}

          {/* Notebook widget on today view too */}
          <NotebookWidget navigate={navigate} />
        </>
      )}
    </div>
  );
}

function SeasonReport() {
  const [seasonId, setSeasonId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!seasonId) { setData(null); return; }
    setLoading(true);
    api.get(`/dashboard/season?seasonId=${seasonId}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [seasonId]);

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <SeasonSelector value={seasonId} onChange={setSeasonId} />
      </div>

      {!seasonId ? (
        <div className="empty-state"><div className="icon">🌱</div><p>Özet için bir sezon seçin</p>
          <p style={{ fontSize: '0.8rem', color: '#7a9472' }}>Sezonları Ayarlar → Sezonlar'dan yönetebilirsiniz.</p></div>
      ) : loading ? (
        <div className="empty-state"><p>Yükleniyor...</p></div>
      ) : !data ? (
        <div className="empty-state"><p>Sezon özeti alınamadı</p></div>
      ) : (
        <>
          {data.seasonName && (
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1c3a1c', marginBottom: 10 }}>🌾 {data.seasonName}</div>
          )}
          <div className="card" style={{
            background: data.netProfit >= 0
              ? 'linear-gradient(135deg, #1c3a1c, #2d5a27)'
              : 'linear-gradient(135deg, #5a1c1c, #8b2020)',
            color: '#fff', marginBottom: 12,
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sezon Net Kâr</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, marginTop: 4 }}>{fmt(data.netProfit)}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: 4 }}>
              {data.sales.count} satış · {data.purchases.count} alım
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="icon">💰</div>
              <div className="label">Toplam Satış</div>
              <div className="value positive">{fmt(data.sales.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">🛒</div>
              <div className="label">Toplam Alım</div>
              <div className="value negative">{fmt(data.purchases.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">👷</div>
              <div className="label">İşçilik Gideri</div>
              <div className="value negative">{fmt(data.labour.total)}</div>
            </div>
            <div className="stat-card">
              <div className="icon">🌾</div>
              <div className="label">Toplam Hasat</div>
              <div className="value">{qty(data.harvest.totalKg)} kg</div>
            </div>
          </div>
          <p style={{ fontSize: '0.72rem', color: '#aaa', marginTop: 8, textAlign: 'center' }}>
            İşçilik, bu sezonun hasatlarına bağlı kayıtları içerir.
          </p>
        </>
      )}
    </div>
  );
}

function UpcomingPaymentsSection() {
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    api.get('/payments/schedule/upcoming')
      .then(r => setUpcoming((r.data.data || []).slice(0, 5)))
      .catch(() => {});
  }, []);

  if (upcoming.length === 0) return null;

  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ marginTop: 12 }}>
      <div className="section-title" style={{ marginBottom: 8 }}>Yaklaşan Ödemeler</div>
      <div className="card">
        {upcoming.map(p => {
          const isOverdue = p.due_date < today;
          const isToday = p.due_date === today;
          const dot = isOverdue ? '🔴' : isToday ? '🟡' : '⚪';
          const days = Math.ceil((new Date(p.due_date) - new Date(today)) / 86400000);
          const label = isOverdue ? `${Math.abs(days)} gün gecikmiş` : isToday ? 'Bugün' : `${days} gün`;
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.82rem' }}>
              <span>{dot}</span>
              <span style={{ color: '#888', minWidth: 60 }}>{label}</span>
              <span style={{ flex: 1, fontWeight: 600 }}>{p.name}</span>
              <span style={{ fontWeight: 700, color: p.entity_type === 'sale' ? '#27ae60' : '#e74c3c' }}>
                {fmt(p.amount)} {p.entity_type === 'sale' ? 'alacak' : 'borç'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NotebookWidget({ navigate }) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get('/notebook')
      .then(r => setNotes((r.data.data || []).slice(0, 5)))
      .catch(() => {});
  }, []);

  if (notes.length === 0) return null;
  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ marginTop: 12 }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px' }}>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1c3a1c' }}>📓 Notlar</span>
          <button onClick={() => navigate('/notebook')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--green-mid)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            Tümü →
          </button>
        </div>
        <div style={{ padding: '0 14px 10px' }}>
          {notes.map(n => {
            const due = n.due_date && n.due_date.split('T')[0];
            const overdueOrToday = !n.is_done && due && due <= today;
            return (
              <div key={n.id} onClick={() => navigate('/notebook')}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '1px solid #f2f2f2', fontSize: '0.84rem', cursor: 'pointer' }}>
                <span>{n.is_done ? '✅' : '⭕'}</span>
                <span style={{ flex: 1, textDecoration: n.is_done ? 'line-through' : 'none', color: n.is_done ? '#999' : '#444' }}>
                  {n.content}
                </span>
                {overdueOrToday && (
                  <span style={{ fontSize: '0.7rem', color: '#e74c3c', fontWeight: 700 }}>
                    🔴 {due === today ? 'Bugün vadeli' : 'Gecikmiş'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// "Bu Ay İşçilik" — taps through to the Yevmiyeler registry
function LabourMonthCard({ navigate }) {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    api.get('/yevmiyeler/summary?period=month')
      .then(r => setSum(r.data))
      .catch(() => {});
  }, []);
  return (
    <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/yevmiyeler')}>
      <div className="icon">👷</div>
      <div className="label">Bu Ay İşçilik</div>
      <div className="value negative">{fmt(sum ? sum.totalAmount : 0)}</div>
      {sum && sum.totalPending > 0 && (
        <div style={{ fontSize: '0.68rem', color: '#e67e22', fontWeight: 700 }}>⏳ {fmt(sum.totalPending)} bekliyor</div>
      )}
    </div>
  );
}

function FavouriteProductsSection() {
  const [favProducts, setFavProducts] = useState([]);

  useEffect(() => {
    api.get('/farm-products?isFavourite=true&includeStock=true')
      .then(r => setFavProducts(r.data.data || []))
      .catch(() => {});
  }, []);

  if (favProducts.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div className="section-title" style={{ marginBottom: 8 }}>⭐ Ana Ürünlerim</div>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
        {favProducts.map(p => {
          const emoji = { crop: '🌾', livestock: '🐄', seed: '🌱', fertilizer: '🧪', pesticide: '💧', fuel: '⛽', material: '🔧' }[p.category] || '📦';
          const q = parseFloat(p.stock_quantity) || 0;
          const displayQty = q >= 1000 ? `${(q / 1000).toFixed(1)} ton` : `${qty(q)} ${p.stock_display_unit || p.unit}`;
          return (
            <div key={p.master_product_id} className="card" style={{
              minWidth: 120, padding: '10px 12px', flexShrink: 0,
            }}>
              <div style={{ fontSize: '1.1rem', marginBottom: 2 }}>{emoji} {p.name.split(' - ')[0]}</div>
              {p.variety && <div style={{ fontSize: '0.7rem', color: '#999' }}>{p.variety}</div>}
              <div style={{ fontWeight: 800, fontSize: '0.85rem', color: q > 0 ? '#2d5a27' : '#ccc', marginTop: 4 }}>
                {q > 0 ? displayQty : 'Stok yok'}
              </div>
              {p.avg_cost_per_unit && q > 0 && (
                <div style={{ fontSize: '0.7rem', color: '#aaa' }}>
                  {fmt(p.avg_cost_per_unit)}/{p.unit}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
